<template>
  <div>
    <!-- ══════════════════════════════════════════════════════
         TABLA PRINCIPAL
    ══════════════════════════════════════════════════════ -->
    <CRow>
      <CCol sm="12">
        <CTableWrapper :items="data" @mtdBack="mtdBack" :fields="fields" hover striped border small fixed
          caption="STOCK">
          <!-- Botones de acción -->
          <template #accion="{ item }">
            <td class="text-center">
              <!-- Eliminar -->
              <CButton color="danger" size="sm" class="mr-1" @click="mtd_delete(item)">
                <i class="fas fa-trash"></i>
              </CButton>
              <!-- Gestión de stock -->
              <CButton color="info" size="sm" @click="mtd_open_stock(item)">
                <i class="fas fa-layer-group"></i>
              </CButton>
            </td>
          </template>
          <!-- Stock formateado -->
          <template #stock_total="{ item }">
            <td>{{ item.stock_total }}</td>
          </template>
          <!-- En uso formateado -->
          <template #stock_abierto="{ item }">
            <td>{{ item.stock_abierto || '—' }}</td>
          </template>
          <!-- Badge estado -->
          <template #status="{ item }">
            <td>
              <CBadge :color="item.state === 1 ? 'success' : 'secondary'">
                {{ item.status }}
              </CBadge>
            </td>
          </template>
        </CTableWrapper>
      </CCol>
    </CRow>

    <!-- ══════════════════════════════════════════════════════
         MODAL — GESTIÓN DE STOCK
    ══════════════════════════════════════════════════════ -->
    <CModal title="Gestión de Stock" :show.sync="stock.show" size="xl" class="modal-stock">
      <!-- Header info del material -->
      <div class="stock-header mb-4" v-if="stock.material">
        <h5 class="font-weight-bold mb-0">{{ stock.material.name }}</h5>
        <small class="text-muted">
          Unidad: <strong>{{ stock.material.unit_name }}</strong>
          <span v-if="stock.material.sub_unit_name">
            · Sub-unidad: <strong>{{ stock.material.sub_unit_name }}</strong>
          </span>
          · Conversión: <strong>{{ stock.material.unit_conversion }}</strong>
        </small>
      </div>
      <!-- ── TARJETAS RESUMEN ─────────────────────────────── -->
      <CRow class="mb-4">
        <!-- Total -->
        <CCol sm="6" md="4">
          <div class="stock-card stock-card--total">
            <div class="stock-card__number">{{ stock.summary.total_sub_units }}</div>
            <div class="stock-card__label">
              Total {{ stock.material ? (stock.material.sub_unit_name || stock.material.unit_name) : '' }}
            </div>
            <div class="stock-card__sub">
              {{ stock.summary.total_units }} {{ stock.material ? stock.material.unit_name : '' }}
            </div>
          </div>
        </CCol>
        <!-- En uso -->
        <CCol sm="6" md="4">
          <div class="stock-card stock-card--opened">
            <div class="stock-card__number">{{ stock.summary.opened_units }}</div>
            <div class="stock-card__label">En uso</div>
            <div class="stock-card__sub" v-if="stock.material && stock.material.sub_unit_name">
              {{ stock.summary.opened_sub_units }} {{ stock.material.sub_unit_name }}
            </div>
          </div>
        </CCol>
        <!-- Vacíos -->
        <CCol sm="6" md="4">
          <div class="stock-card stock-card--empty">
            <div class="stock-card__number">{{ stock.empty_count }}</div>
            <div class="stock-card__label">Vacíos</div>
          </div>
        </CCol>
      </CRow>
      <!-- ── ACCIONES RÁPIDAS ────────────────────────────── -->
      <CRow class="mb-4">
        <!-- Agregar unidades -->
        <CCol sm="12" md="6">
          <CCard class="stock-action-card">
            <CCardHeader class="stock-action-card__header">
              <i class="fas fa-plus mr-1"></i>
              Agregar unidades al stock
            </CCardHeader>
            <CCardBody>
              <CInput v-model="stock.add_quantity" type="number" min="1" placeholder="Cantidad a agregar"
                @input="validateIntegerStock('add_quantity')" />
              <CButton color="success" :disabled="!stock.add_quantity || stock.loading" @click="mtd_addItems">
                <span v-if="stock.loading">
                  <CSpinner size="sm" class="mr-1" /> Agregando…
                </span>
                <span v-else>Agregar</span>
              </CButton>
              <small class="text-muted">
                Cada unidad ingresa con
                <strong>{{ stock.material ? stock.material.unit_conversion : '' }}</strong>
                {{ stock.material ? stock.material.sub_unit_name || stock.material.unit_name : '' }}
                completas.
              </small>
            </CCardBody>
          </CCard>
        </CCol>
        <!-- Consumir sub-unidades (solo si hay stock disponible) -->
        <CCol sm="12" md="6" v-if="stock.summary.total_sub_units > 0">
          <CCard class="stock-action-card">
            <CCardHeader class="stock-action-card__header">
              <i class="fas fa-arrow-down mr-1"></i>
              Consumir {{ stock.material ? stock.material.sub_unit_name || stock.material.unit_name : 'unidades' }}
            </CCardHeader>
            <CCardBody>
              <!-- Info disponible -->
              <div class="consume-info mb-2">
                <i class="fas fa-info-circle mr-1 text-info"></i>
                Disponible:
                <strong>{{ stock.summary.total_sub_units }}</strong>
                {{ stock.material ? stock.material.sub_unit_name || stock.material.unit_name : '' }}
              </div>
              <CInput v-model="stock.consume_amount" type="number" min="1"
                :max="stock.summary.total_sub_units"
                placeholder="Cantidad a consumir" @input="validateDecimalStock('consume_amount')" />
              <CButton color="danger" :disabled="!stock.consume_amount || stock.loading"
                @click="mtd_consume">
                <span v-if="stock.loading">
                  <CSpinner size="sm" class="mr-1" /> Consumiendo…
                </span>
                <span v-else>Consumir</span>
              </CButton>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <template #footer>
        <CButton color="secondary" @click="stock.show = false">Cerrar</CButton>
      </template>
    </CModal>

    <!-- ══════════════════════════════════════════════════════
         MODAL — CONFIRMACIÓN ELIMINAR
    ══════════════════════════════════════════════════════ -->
    <CModal title="Eliminar Material" :show.sync="modal_delete.show" color="danger">
      <p>
        ¿Estás seguro que deseas eliminar
        <strong>{{ modal_delete.item ? modal_delete.item.name : '' }}</strong>?
      </p>
      <template #footer>
        <CButton color="secondary" @click="modal_delete.show = false">Cancelar</CButton>
        <CButton color="danger" @click="mtd_commit">Eliminar</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import CTableWrapper from "../../../components/shared/datatable/Table.vue";

export default {
  components: { CTableWrapper },
  name: 'Material',

  // ──────────────────────────────────────────────────────────
  // Data
  // ──────────────────────────────────────────────────────────
  data() {
    return {
      // ── Tabla principal ──────────────────────────────────
      data: [],
      fields: [
        { key: 'index', label: '#', _style: 'width:50px' },
        { key: 'name', label: 'Nombre' },
        { key: 'unit_name', label: 'Unidad' },
        { key: 'stock_total', label: 'Stock' },
        { key: 'price_total', label: 'Precio' },
        { key: "precio_total_stock", label: "Precio Total", _style: "width:12%;" },
        { key: 'status', label: 'Estado' },
        { key: 'accion', label: 'Acción', _style: 'width:100px;text-align:center' },
      ],
      // ── Modal eliminar ───────────────────────────────────
      modal_delete: {
        show: false,
        item: null,
      },
      // ── Modal stock ──────────────────────────────────────
      stock: {
        company_id: this.$store.getters.get_company.id,
        campu_id: this.$store.getters.get_campu.id,
        user_id: this.$store.getters.get__user_id,
        show: false,
        loading: false,
        material: null,
        items: [],
        empty_count: 0,
        summary: {
          total_units: 0,
          total_sub_units: 0,
          closed_units: 0,
          closed_sub_units: 0,
          opened_units: 0,
          opened_sub_units: 0,
        },
        add_quantity: '',
        consume_amount: '',
      },
    }
  },

  // ──────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────
  mounted() {
    this.mtd_getdata()
  },

  // ──────────────────────────────────────────────────────────
  // Methods
  // ──────────────────────────────────────────────────────────
  methods: {
    ...mapActions(['get', 'post', 'put', 'destroy']),

    // ── Carga inicial ──────────────────────────────────────
    mtd_getdata() {
      const company_id = this.$store.getters.get_company.id;
      const campu_id = this.$store.getters.get_campu.id;
      this.get({
        url: `${this.$store.getters.get__url}/material/index/${company_id}/${campu_id}`,
        token: this.$store.getters.get__token,
      })
        .then((response) => {
          if (response.statusCode == 200) {
            this.data = response.data.data.map((item, index) => {
              const s = item.stock
              const subUnit = item.sub_unit_name || item.unit_name
              const stock_total = `${s.total_sub_units} ${subUnit}`
              let stock_abierto = null
              if (s.opened_units > 0) {
                stock_abierto = `${s.opened_sub_units} ${subUnit} en uso`
              }
              const precio_total_stock = (parseFloat(s.total_units || 0) * parseFloat(item.price_total || 0)).toFixed(2);
              return {
                ...item,
                index: index + 1,
                stock_total,
                stock_abierto,
                precio_total_stock,
              }
            })
          }
        })
        .catch(() => Swal.fire({ title: "Error en la operación", icon: "error" }));
    },

    // ── Eliminar ───────────────────────────────────────────
    mtd_delete(item) {
      this.modal_delete.item = item
      this.modal_delete.show = true
    },
    async mtd_commit() {
      try {
        const res = await this.post({
          url: `${this.$store.getters.get__url}/material/destroy`,
          token: this.$store.getters.get__token,
          params: { id: this.modal_delete.item.Id },
        })
        if (res.state === 2) {
          Swal.fire({
            title: 'No se puede eliminar',
            text: 'Este material está siendo usado en un tratamiento activo.',
            type: 'warning',
          })
          return
        }
        this.modal_delete.show = false
        await this.mtd_getdata()
        Swal.fire({ title: 'Eliminado', text: 'Material eliminado.', type: 'success' })
      } catch (e) {
        console.error(e)
      }
    },

    // ══════════════════════════════════════════════════════
    // STOCK
    // ══════════════════════════════════════════════════════
    async mtd_open_stock(item) {
      this.stock.show = true
      this.stock.loading = true
      this.stock.material = null
      this.stock.items = []
      this.stock.add_quantity = ''
      this.stock.consume_amount = ''
      this.stock.summary = {
        total_units: 0, total_sub_units: 0,
        closed_units: 0, closed_sub_units: 0,
        opened_units: 0, opened_sub_units: 0,
      }
      try {
        const res = await this.get({
          url: `${this.$store.getters.get__url}/material/show/${item.Id}`,
          token: this.$store.getters.get__token,
        })
        this.stock.material = res.data
        this.stock.items = res.data.stock_items || []
        this.stock.empty_count = this.stock.items.filter(i => i.status === 'empty').length
        const s = res.data.stock
        this.stock.summary = {
          total_units: s.total_units,
          total_sub_units: s.total_sub_units,
          closed_units: s.closed_units,
          closed_sub_units: s.closed_sub_units,
          opened_units: s.opened_units,
          opened_sub_units: s.opened_sub_units,
        }
      } catch (e) {
        console.error('Error cargando stock', e)
      } finally {
        this.stock.loading = false
      }
    },
    async mtd_reloadStock() {
      if (!this.stock.material) return
      const id = this.stock.material.Id || this.stock.material.id
      await this.mtd_open_stock({ Id: id })
      await this.mtd_getdata()
    },
    async mtd_addItems() {
      if (!this.stock.add_quantity || parseInt(this.stock.add_quantity) < 1) return
      this.stock.loading = true
      try {
        const res = await this.post({
          url: `${this.$store.getters.get__url}/material/stock/add-items-simple`,
          token: this.$store.getters.get__token,
          params: {
            material_id: this.stock.material.Id,
            quantity: parseInt(this.stock.add_quantity),
          },
        })
        if (res.data.state === 0) {
          this.stock.add_quantity = ''
          await this.mtd_reloadStock()
          Swal.fire({ title: 'Stock actualizado', text: 'Ítems agregados correctamente.', icon: 'success' })

        }
      } catch (e) {
        console.error(e)
      } finally {
        this.stock.loading = false
      }
    },
    async mtd_consume() {
      if (!this.stock.consume_amount) return
      this.stock.loading = true
      try {
        const res = await this.post({
          url: `${this.$store.getters.get__url}/material/stock/consume-item`,
          token: this.$store.getters.get__token,
          params: {
            material_id: this.stock.material.Id || this.stock.material.id,
            amount: parseFloat(this.stock.consume_amount),
          },
        })
        if (res.data.state === 0) {
          this.stock.consume_amount = ''
          await this.mtd_reloadStock()
          Swal.fire({ title: 'Consumido', text: 'Unidades descontadas correctamente.', icon: 'success' })
        }
      } catch (e) {
        Swal.fire({
          title: 'Error al consumir',
          text: e.response?.data?.error_message || 'Cantidad supera el stock disponible.',
          icon: 'error',
        })
      } finally {
        this.stock.loading = false
      }
    },

    // ── Validaciones de stock ─────────────────────────────
    validateIntegerStock(field) {
      let v = parseInt(this.stock[field])
      if (isNaN(v) || v < 1) v = ''
      this.stock[field] = v
    },
    validateDecimalStock(field) {
      let v = String(this.stock[field])
      v = v.replace(/[^0-9.]/g, '')
      const parts = v.split('.')
      if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('')
      if (parts[1] && parts[1].length > 2) v = parts[0] + '.' + parts[1].slice(0, 2)
      this.stock[field] = v
    },

    mtdBack() {
      this.$emit("returnHome");
    },
  },
}
</script>

<style scoped>
/* ── Tarjetas resumen de stock ─────────────────────────── */
.stock-card {
  border-radius: 10px;
  padding: 16px 20px;
  text-align: center;
  color: #fff;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .12);
}

.stock-card--total {
  background: linear-gradient(135deg, #3c8dbc, #005f8e);
}

.stock-card--closed {
  background: linear-gradient(135deg, #6c757d, #495057);
}

.stock-card--opened {
  background: linear-gradient(135deg, #f0a500, #c47d00);
}

.stock-card--empty {
  background: linear-gradient(135deg, #e74c3c, #a93226);
}

.stock-card__number {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}

.stock-card__label {
  font-size: .8rem;
  text-transform: uppercase;
  letter-spacing: .06em;
  margin-top: 4px;
  opacity: .9;
}

.stock-card__sub {
  font-size: .78rem;
  margin-top: 2px;
  opacity: .8;
}

/* ── Header del modal de stock ─────────────────────────── */
.stock-header {
  padding: 10px 14px;
  background: #f8f9fa;
  border-left: 4px solid #3c8dbc;
  border-radius: 4px;
}

/* ── Cards de acciones ─────────────────────────────────── */
.stock-action-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  height: 100%;
}

.stock-action-card__header {
  background: #f8f9fa;
  font-weight: 600;
  font-size: .9rem;
  padding: 10px 16px;
}

.consume-info {
  padding: 6px 10px;
  background: #e8f4fd;
  border-radius: 6px;
  border: 1px solid #bee5eb;
  font-size: .88rem;
}

/* ── Tabla de ítems (removida, estilos ya no usados) ── */
</style>